import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { isArray } from 'lodash-es'

import { ADHOC_EVENT_TYPE, MANAGED_EVENT_TYPE } from 'constants/event_types'

export const formatDuration = (duration, durationOptions, convert60 = false) => {
  let formattedDuration = ''

  if (duration === 60 && convert60) {
    formattedDuration = `1 ${I18n.t('time.hours_abbr.one')}`
  } else if (duration % 60 === 0 && duration > 60) {
    const durationInHours = duration / 60
    formattedDuration = `${durationInHours} ${I18n.t('time.hours_abbr.one')}`
  } else if (duration > 60) {
    const minutes = duration % 60
    const hours = Math.floor(duration / 60)
    formattedDuration = `${hours} ${I18n.t('time.hours_abbr.one')} ${minutes} ${I18n.t('time.min_abbr')}`
  } else {
    formattedDuration = `${duration} ${I18n.t('time.min_abbr')}`
  }

  if (isArray(durationOptions) && durationOptions.length > 1) {
    const optionsCount = durationOptions.filter((d) => d !== duration).length
    const optionsSummary = I18n.t('publisher.dashboard.event.count_more', { count: optionsCount })
    formattedDuration = formattedDuration.concat(` ${optionsSummary}`)
  }

  return formattedDuration
}

export const formatDurationSummary = (duration, durationOptions, convert60 = false) => {
  if (durationOptions?.length > 1) {
    const defaultDurationLabel = I18n.t(
      'publisher.event_type_editor.sections.event_details.duration_summary.default_duration_label',
      { duration_label: formatDuration(duration, undefined, convert60) }
    )
    const otherDurations = durationOptions
      .slice()
      .sort((a, b) => a - b)
      .filter((d) => d !== duration).map((d) => formatDuration(d, undefined, convert60))
    const lastDuration = otherDurations.pop()
    const lastDurationLabel = I18n.t(
      'publisher.event_type_editor.sections.event_details.duration_summary.last_duration_label',
      { duration_label: lastDuration }
    )
    const durationLabels = [defaultDurationLabel, ...otherDurations, lastDurationLabel]

    return durationLabels.length > 2 ? durationLabels.join(', ') : durationLabels.join(' ')
  }

  return formatDuration(duration, undefined, convert60)
}

export const getPoolingTypeKinds = (poolingType) => ({
  isCollective: poolingType === 'collective',
  isRoundRobin: poolingType === 'round_robin',
  isCollectiveRoundRobin: poolingType === 'multi_pool',
  isMultiHost: Boolean(poolingType)
})

export const withKindsAndTypes = (eventType) => (eventType ? {
  ...eventType,
  ...getPoolingTypeKinds(eventType.pooling_type),
  isSolo: eventType.kind === 'solo',
  isGroup: eventType.kind === 'group',
  isUser: eventType.owner_type === 'User',
  isTeam: eventType.owner_type === 'Page',
  isShared: eventType.owner_type === 'Organization',
  isPoll: eventType.booking_method === 'poll',
  isAdhocEventType: eventType.type === ADHOC_EVENT_TYPE,
  isMETParent: eventType.type === MANAGED_EVENT_TYPE,
  isMETChild: Boolean(eventType.parent_id)
} : null)

export const useETEditorFormikPoolingType = () => {
  const { values: { pooling_type: poolingType } } = useFormContext()

  return getPoolingTypeKinds(poolingType)
}


export const getKindDescription = ({ isAdhocEventType, isCollective, isRoundRobin, isCollectiveRoundRobin, isGroup, isSolo }) => {
  if (isCollectiveRoundRobin) { return I18n.t('publisher.event_types.kind.collective_round_robin.name') }

  if (isRoundRobin) { return I18n.t('publisher.event_types.kind.round_robin.name') }

  if (isCollective) { return I18n.t('publisher.event_types.kind.collective.name') }

  if (isGroup) { return I18n.t('publisher.event_types.kind.group.name') }

  if (isSolo) { return I18n.t('publisher.event_types.kind.one_on_one.name') }

  if (isAdhocEventType) { return I18n.t('publisher.labels.meeting_poll') }

  return null
}
